import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { queryKeys } from "../lib/query/keys";
import {
  notificationsApi,
  type Notification,
  type PaginatedNotifications,
  type SentNotification,
  type Position,
  type CreateNotificationData,
  type UpdateNotificationData,
  type NotificationStatus,
} from "@/lib/api/notifications";

import { withQuerySignal } from "@/lib/api";
// ============ QUERIES ============

/**
 * Fetch current user's notifications
 */
export function useMyNotifications(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: queryKeys.notifications.my(params),
    queryFn: ({ signal }) => withQuerySignal(signal, () => notificationsApi.getMyNotifications(params)),
    staleTime: 30 * 1000,
    select: (response) => response.data,
  });
}

/**
 * Fetch notifications with infinite scroll pagination
 */
export function useInfiniteNotifications(limit = 20) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.notifications.all, "infinite", limit],
    queryFn: ({ pageParam = 0 }) =>
      notificationsApi.getMyNotifications({ limit, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + limit : undefined,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch unread notification count with polling
 */
export function useUnreadNotificationCount(options?: {
  pollingInterval?: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationsApi.getUnreadCount(),
    refetchInterval: options?.pollingInterval ?? false, // SSE handles real-time updates
    staleTime: 10 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch notifications sent by current user
 */
export function useSentNotifications() {
  return useQuery({
    queryKey: queryKeys.notifications.sent(),
    queryFn: () => notificationsApi.getSentNotifications(),
  });
}

/**
 * Fetch positions for notification form dropdown
 */
export function useNotificationPositions() {
  return useQuery({
    queryKey: queryKeys.notifications.positions(),
    queryFn: () => notificationsApi.getPositions(),
    staleTime: 5 * 60 * 1000,
  });
}

// ============ MUTATIONS ============

/**
 * Create notification mutation
 */
export function useCreateNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNotificationData) => notificationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.sent() });
    },
  });
}

/**
 * Update notification mutation
 */
export function useUpdateNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateNotificationData }) =>
      notificationsApi.update(id, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.sent() });
    },
  });
}

/**
 * Delete notification mutation
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => notificationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.sent() });
    },
  });
}

/**
 * Mark notification status (viewed/deleted)
 */
export function useMarkNotificationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: NotificationStatus }) =>
      notificationsApi.markStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Mark all notifications as read with optimistic update
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: async () => {
      // Optimistically set unread count to 0
      await queryClient.cancelQueries({
        queryKey: queryKeys.notifications.unreadCount(),
      });

      const previousCount = queryClient.getQueryData<{ count: number }>(
        queryKeys.notifications.unreadCount()
      );

      queryClient.setQueryData(queryKeys.notifications.unreadCount(), {
        count: 0,
      });

      return { previousCount };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousCount) {
        queryClient.setQueryData(
          queryKeys.notifications.unreadCount(),
          context.previousCount
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

// Re-export types
export type {
  Notification,
  PaginatedNotifications,
  SentNotification,
  Position,
  CreateNotificationData,
  UpdateNotificationData,
  NotificationStatus,
};
